/*
 * One removable row of a Box exclusion list, shared by the list editor and the auto-added defaults
 * group so an entry looks and removes the same wherever it is shown. Test ids carry the entry's
 * index in the full draft list, which is also the index `onRemove` acts on.
 */
import React from 'react';
import { css } from '@emotion/css';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { colorGray50 } from '@amzn/meridian-tokens/base/color';
import closeSmallTokens from '@amzn/meridian-tokens/base/icon/close-small';
import { useTranslation } from 'src/arb-wrapper';
import { getMatchModeLabelKey } from 'src/components/box-folders/PathnameMatchModeControl';
import { type ExclusionEntry } from 'src/utils/exclusionEntries';
import { type PathnameMatch } from 'src/utils/pathnameMatchMode';

/** An entry paired with its position in the full draft list, so a display subset still removes the right one. */
export interface IndexedExclusionEntry {
  entry: ExclusionEntry;
  index: number;
}

const entryRowStyles = css({
  borderRadius: 4,
  '&:hover': {
    backgroundColor: colorGray50,
  },
});

interface ExclusionEntryRowProps {
  entry: ExclusionEntry;
  index: number;
  isDisabled: boolean;
  /** When given, the row shows the match text with its mode beneath instead of the raw value. */
  match?: PathnameMatch;
  testIdPrefix: string;
  onRemove: () => void;
}

export const ExclusionEntryRow: React.FC<ExclusionEntryRowProps> = ({
  entry,
  index,
  isDisabled,
  match,
  testIdPrefix,
  onRemove,
}) => {
  const { t } = useTranslation();

  return (
    <Row
      alignmentVertical="center"
      className={entryRowStyles}
      data-testid={`${testIdPrefix}-entry-row-${index}`}
      spacing="200"
      spacingInset="100 200"
      widths={['fill', 'fit']}
    >
      <Column minWidth={0} spacing="100">
        <Text
          breakWord={true}
          data-testid={`${testIdPrefix}-entry-${index}`}
          tag="div"
        >
          {match?.text ?? entry.value}
        </Text>
        {match !== undefined && (
          <Text color="secondary" data-testid={`${testIdPrefix}-entry-mode-${index}`} type="b200">
            {t(getMatchModeLabelKey(match.mode))}
          </Text>
        )}
      </Column>
      <Button
        aria-label={t('box_folder_exclusions_remove_entry', { entry: entry.value })}
        data-testid={`${testIdPrefix}-remove-${index}`}
        disabled={isDisabled}
        size="small"
        type="icon"
        onClick={onRemove}
      >
        <Icon tokens={closeSmallTokens} />
      </Button>
    </Row>
  );
};
